import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { History, ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox';
import { EmptyState } from '@/components/ui/EmptyState';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import { SaveBar, pluralizeChanges } from '@/components/ui/SaveBar';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { UnsavedChangesGuard } from '@/components/system/UnsavedChangesGuard';
import {
  useAdminBIPrincipals,
  useAdminBIResources,
  useRoleGrants,
  useSaveRoleGrants,
  useSaveUserGrants,
  useUserGrants,
  type AccessPermission,
  type GrantState,
} from '@/api/bi/access';
import { useAuth } from '@/auth/AuthProvider';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { AccessAuditSheet } from './AccessAuditSheet';
import { AccessMatrix } from './AccessMatrix';
import {
  PERMISSIONS,
  buildDraft,
  buildResourceTree,
  changedCellCount,
  defaultCellState,
  draftToGrantRows,
  isPrivilegedRole,
  type GrantDraft,
} from './grants';

/**
 * Access control.
 *
 * Used to be a card buried at the bottom of a 541-line `/settings` page, which
 * meant the densest, most consequential screen in the app shared a scroll
 * container with the theme toggle. It now has its own route so the matrix can
 * own the viewport and pin its own header.
 */
export default function AccessControlPage() {
  const { user } = useAuth();
  const enabled = !!user?.is_superuser;

  const resources = useAdminBIResources(enabled);
  const principals = useAdminBIPrincipals(enabled);

  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedUserId = useMemo(() => {
    const raw = searchParams.get('user');
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  const [mode, setMode] = useState<'role' | 'user'>(deepLinkedUserId != null ? 'user' : 'role');
  const [role, setRole] = useState<string | null>(null);
  const [userId, setUserId] = useState<number | null>(deepLinkedUserId);
  const [auditOpen, setAuditOpen] = useState(false);

  useEffect(() => {
    if (!role && principals.data?.roles?.length) setRole(principals.data.roles[0].id);
  }, [principals.data?.roles, role]);

  useEffect(() => {
    if (!userId && principals.data?.users?.length) setUserId(principals.data.users[0].id);
  }, [principals.data?.users, userId]);

  // Honour `?user=` once. After that the admin's own selection wins, so we drop
  // the param rather than letting it re-apply on every render.
  const appliedDeepLink = useRef(false);
  useEffect(() => {
    if (deepLinkedUserId == null || appliedDeepLink.current) return;
    appliedDeepLink.current = true;
    setMode('user');
    setUserId(deepLinkedUserId);
    const next = new URLSearchParams(searchParams);
    next.delete('user');
    setSearchParams(next, { replace: true });
  }, [deepLinkedUserId, searchParams, setSearchParams]);

  const roleGrants = useRoleGrants(mode === 'role' ? role : null);
  const userGrants = useUserGrants(mode === 'user' ? userId : null);
  const activeGrants = mode === 'role' ? roleGrants : userGrants;

  const targetUser = useMemo(
    () => principals.data?.users.find((u) => u.id === userId),
    [principals.data?.users, userId],
  );
  const targetRole = mode === 'role' ? role : (targetUser?.role ?? null);
  const privileged = isPrivilegedRole(targetRole);
  const cellDefault = defaultCellState(privileged);

  // In user mode we also need the role's grants, because a cell left on
  // `inherit` is decided entirely by them.
  const inheritedRoleGrants = useRoleGrants(mode === 'user' ? (targetUser?.role ?? null) : null);
  const roleGrantBySlug = useMemo(() => {
    const map = new Map<string, Partial<Record<AccessPermission, GrantState>>>();
    for (const row of inheritedRoleGrants.data?.grants ?? []) map.set(row.resource_slug, row.permissions);
    return map;
  }, [inheritedRoleGrants.data?.grants]);

  const ordered = useMemo(
    () => buildResourceTree(resources.data?.resources ?? []),
    [resources.data?.resources],
  );

  /*
   * `baseline` is the draft as loaded. Keeping it lets us show a change count,
   * offer Discard and warn on navigation — none of which the old single Save
   * button could do, since it had no idea what had been touched.
   */
  const [draft, setDraft] = useState<GrantDraft>({});
  const [baseline, setBaseline] = useState<GrantDraft>({});
  const [storeIds, setStoreIds] = useState<string[]>([]);
  const [baselineStoreIds, setBaselineStoreIds] = useState<string[]>([]);

  const dirtyCells = useMemo(() => changedCellCount(draft, baseline), [draft, baseline]);
  const storesDirty = useMemo(
    () =>
      storeIds.length !== baselineStoreIds.length ||
      storeIds.some((id) => !baselineStoreIds.includes(id)),
    [storeIds, baselineStoreIds],
  );
  const isDirty = dirtyCells > 0 || storesDirty;

  const hydrate = useCallback(() => {
    if (!ordered.length) return;
    const next = buildDraft(ordered, activeGrants.data?.grants, cellDefault);
    const nextStores = (activeGrants.data?.store_ids ?? []).map(String);
    setDraft(next);
    setBaseline(next);
    setStoreIds(nextStores);
    setBaselineStoreIds(nextStores);
  }, [ordered, activeGrants.data, cellDefault]);

  /*
   * Load server state into the draft when the target changes — but never on top
   * of unsaved edits. React Query refetches on window focus, so without this
   * guard alt-tabbing away and back would quietly discard everything the admin
   * had just set.
   */
  const targetKey = `${mode}:${mode === 'role' ? (role ?? '') : (userId ?? '')}`;
  const hydratedKey = useRef<string | null>(null);
  const dirtyRef = useRef(false);
  dirtyRef.current = isDirty;

  useEffect(() => {
    if (!ordered.length || !activeGrants.data) return;
    const targetChanged = hydratedKey.current !== targetKey;
    if (!targetChanged && dirtyRef.current) return;
    hydratedKey.current = targetKey;
    hydrate();
  }, [hydrate, targetKey, ordered.length, activeGrants.data]);

  const saveRole = useSaveRoleGrants(role);
  const saveUser = useSaveUserGrants(userId);
  const isSaving = saveRole.isPending || saveUser.isPending;

  const setCell = useCallback((slug: string, permission: AccessPermission, state: GrantState) => {
    setDraft((current) => ({ ...current, [slug]: { ...(current[slug] ?? {}), [permission]: state } }));
  }, []);

  const setRows = useCallback((slugs: string[], state: 'allow' | 'deny') => {
    setDraft((current) => {
      const next = { ...current };
      for (const slug of slugs) {
        next[slug] = Object.fromEntries(PERMISSIONS.map((p) => [p, state])) as Record<
          AccessPermission,
          GrantState
        >;
      }
      return next;
    });
  }, []);

  const setColumn = useCallback((permission: AccessPermission, state: GrantState, slugs: string[]) => {
    setDraft((current) => {
      const next = { ...current };
      for (const slug of slugs) {
        next[slug] = { ...(next[slug] ?? {}), [permission]: state };
      }
      return next;
    });
  }, []);

  async function save() {
    const grants = draftToGrantRows(ordered, draft, cellDefault);
    const selectedStoreIds = storeIds.map(Number).filter((id) => Number.isFinite(id));
    try {
      if (mode === 'role') await saveRole.mutateAsync({ grants, storeIds: selectedStoreIds });
      else await saveUser.mutateAsync({ grants, storeIds: selectedStoreIds });
      // Adopt the saved draft immediately so the page stops looking dirty
      // before the refetch lands.
      setBaseline(draft);
      setBaselineStoreIds(storeIds);
      notify.success(`BI access saved for ${targetLabel}`);
    } catch (err) {
      notify.error('Could not save BI access', {
        description: apiErrorMessage(err, 'The grant update was rejected.'),
      });
    }
  }

  const targetLabel =
    mode === 'role'
      ? (principals.data?.roles.find((r) => r.id === role)?.label ?? role ?? 'role')
      : (targetUser?.name || targetUser?.username || (userId ? `user #${userId}` : 'user'));

  const roleOptions = useMemo<ComboboxOption[]>(
    () => (principals.data?.roles ?? []).map((r) => ({ value: r.id, label: r.label })),
    [principals.data?.roles],
  );

  const userOptions = useMemo<ComboboxOption[]>(
    () =>
      (principals.data?.users ?? []).map((u) => ({
        value: String(u.id),
        label: u.name || u.username || u.phone_number || `User #${u.id}`,
        hint: u.role,
      })),
    [principals.data?.users],
  );

  if (!enabled) {
    return (
      <EmptyState
        title="Superuser only"
        description="BI access grants can only be edited by a superuser account."
      />
    );
  }

  return (
    <div className="pb-24">
      <UnsavedChangesGuard
        when={isDirty}
        description={`${dirtyCells} permission change${dirtyCells === 1 ? '' : 's'} on ${targetLabel} have not been saved.`}
      />

      <PageHeader
        eyebrow="Administration"
        title="Access Control"
        description="Grant BI pages, cards, exports and schedule management to a role, or override a single person. User overrides win over their role; anything left on Inherit falls back to the role, then to deny."
        actions={
          <>
            <Badge tone="info" className="gap-1">
              <ShieldCheck className="h-3 w-3" aria-hidden /> superuser
            </Badge>
            <Button size="sm" variant="outline" onClick={() => setAuditOpen(true)}>
              <History className="h-3.5 w-3.5" aria-hidden /> History
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Who are you editing?</CardTitle>
          <Badge tone="neutral" className="capitalize">
            {targetLabel}
          </Badge>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl
              value={mode}
              onChange={(next) => setMode(next)}
              ariaLabel="Grant target type"
              options={[
                { value: 'role', label: 'Role grants' },
                { value: 'user', label: 'User overrides' },
              ]}
            />

            {mode === 'role' ? (
              <Combobox
                value={role}
                onChange={(v) => {
                  if (v != null) setRole(v);
                }}
                options={roleOptions}
                placeholder="Select role"
                searchPlaceholder="Search roles…"
                emptyMessage="No matching roles"
                showClearOption={false}
                loading={principals.isLoading}
                width={220}
                triggerClassName="h-8 min-w-[12rem] text-xs"
              />
            ) : (
              <Combobox
                value={userId != null ? String(userId) : null}
                onChange={(v) => {
                  if (v != null) setUserId(Number(v));
                }}
                options={userOptions}
                placeholder="Select user"
                searchPlaceholder="Search users…"
                emptyMessage="No matching users"
                showClearOption={false}
                loading={principals.isLoading}
                width={320}
                triggerClassName="h-8 min-w-[16rem] text-xs"
              />
            )}

            <p className="text-xs text-muted-foreground">
              Unset permissions default to{' '}
              <span className="font-medium text-foreground">{cellDefault}</span> for{' '}
              {targetRole ? <span className="capitalize">{targetRole}</span> : 'this target'}.
            </p>
          </div>

          <div className="rounded-md border border-border bg-surface px-3 py-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Store access
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <MultiCombobox
                values={storeIds}
                onChange={setStoreIds}
                options={(principals.data?.stores ?? []).map((store) => ({
                  value: String(store.id),
                  label: store.name,
                }))}
                placeholder="Select stores"
                allLabel="No stores assigned"
                countNoun="store"
                width={280}
                loading={principals.isLoading}
              />
              <p className="max-w-xl text-xs text-muted-foreground">
                Only the selected stores are readable for this {mode}. Empty means no BI store access once
                saved. User selections override role selections.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Resources</CardTitle>
        </CardHeader>
        <CardBody>
          <AccessMatrix
            ordered={ordered}
            draft={draft}
            baseline={baseline}
            cellDefault={cellDefault}
            mode={mode}
            roleGrantBySlug={roleGrantBySlug}
            isLoading={resources.isLoading || activeGrants.isLoading}
            isError={resources.isError || activeGrants.isError}
            onRetry={() => {
              void resources.refetch();
              void activeGrants.refetch();
            }}
            onCell={setCell}
            onRow={setRows}
            onColumn={setColumn}
          />
        </CardBody>
      </Card>

      <SaveBar
        visible={isDirty}
        summary={[
          dirtyCells > 0 ? pluralizeChanges(dirtyCells, 'permission change') : null,
          storesDirty ? 'store access changed' : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        context={`for ${targetLabel}`}
        isSaving={isSaving}
        onSave={() => void save()}
        onDiscard={hydrate}
      />

      <AccessAuditSheet open={auditOpen} onOpenChange={setAuditOpen} enabled={enabled} />
    </div>
  );
}

